import { observer } from 'mobx-react-lite'
import Nango from '@nangohq/frontend'
import classNames from 'classnames'
import { useState } from 'react'
import { Button, toastStore } from 'shared/ui'
import { logger } from 'shared/lib'
import { uiStore } from 'shared/store/uiStore'
import { type Integration, IntegrationsApi } from 'entities/Integrations'
import { SettingsRoutes } from 'pages/settings/route/type'
import { IntegrationsRoutes } from 'pages/settings/pages/integrations/route/type'
import styles from './styles.module.scss'

type INangoConnectButtonProps = {
  integration: Integration
  redirectEnabled?: boolean
  hideButton?: boolean
  isIntegrationForbidden: boolean
}

export const NangoConnectButton = observer(
  ({
    integration,
    redirectEnabled = true,
    hideButton = false,
    isIntegrationForbidden,
  }: INangoConnectButtonProps) => {
    const [loading, setLoading] = useState(false)
    const onConnect = async () => {
      setLoading(true)
      const nango = new Nango()
      const connect = nango.openConnectUI({
        onEvent: async (event) => {
          if (event.type === 'close') {
            setLoading(false)
          } else if (event.type === 'connect') {
            try {
              await IntegrationsApi.createInstallation(integration.key, event.payload.connectionId)
              if (redirectEnabled) {
                uiStore.changeRoute({
                  path: `/${SettingsRoutes.root}/${IntegrationsRoutes.root}/${integration.key}/${IntegrationsRoutes.settings}`,
                })
              }
              connect.close()

              toastStore.add({
                type: 'success',
                title: `${integration.name} connected successfully`,
              })
            } catch (e) {
              toastStore.add({
                type: 'error',
                title: 'Something went wrong, try again!',
              })
              logger.error(e)
            }
          }
        },
      })
      try {
        const { data } = await IntegrationsApi.getConnectData(integration.key)
        connect.setSessionToken(data.data?.token) // A loading indicator is shown until this is set.
      } catch (e) {
        setLoading(false)
        connect.close()
        logger.error(e)
        toastStore.add({
          type: 'error',
          title: 'Something went wrong, try again!',
        })
      }
    }

    return (
      <Button
        className={classNames({
          [styles.hidden]: hideButton,
        })}
        loading={loading}
        text='Connect'
        onClick={onConnect}
        iconProps={{ icon: 'plug' }}
        size='medium'
        disabled={isIntegrationForbidden}
        tooltipProps={{
          visibility: isIntegrationForbidden ? 'visible' : 'hidden',
          label:
            'Disabled for HIPAA compliance. Contact Support if you need help or have questions about this integration.',
          placement: 'top',
          maxWidth: '356px',
          breakWord: true,
        }}
      />
    )
  }
)
